import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { fileHref } from '/imports/api/files';
import { toastOnError } from '/imports/ui/utils';
import { Button } from '/imports/ui/Button';
import { ListMethodCatalog } from '/imports/ui/ListMethodCatalog';
import { randomString } from '/imports/utils';
import { Icon } from '/imports/ui/Icon';
import { ConfirmModal } from '/imports/ui/ConfirmModal';
import { ProjectDetailsProps, ProjectDetailsStyled } from '/imports/ui/ProjectViewDetails';
import { Input } from '/imports/ui/Input';
import { Loading } from '/imports/ui/Loading';
import { DateInput } from '/imports/ui/DateInput';
import { NumberInput } from '/imports/ui/NumberInput';
import { MILLIS_1_MIN } from '/imports/constants';
import {
  generateAgreement, ProjectAgreement, modelClass as projectAgreementClass,
  projectAgreementType, sendAgreement
} from '/imports/api/projectAgreements';

const AgreementsSectionStyled = styled.div`
  margin-top: 1rem;
  .list-wrapper {
    margin-top: 2rem;
    width: 100%;
    max-height: 25rem;
    display: flex;
    .row-select, .row-actions {
      display: none;
    }
    .actions {
      display: flex;
      gap: 0.5rem;
    }
  }
  hr {
    margin-top: 2rem;
  }
  .agreement-buttons {
    display: flex;
    gap: 2rem;
  }
  .catalog {
    width: 100%;
    height: 100%;
    max-height: 25rem;
  }
`;

/**
 * Project agreements panel: customer details, agreement generation, and the agreements list.
 */
export const AgreementsSection = (props: ProjectDetailsProps) => {
  const { project, update, setUpdate, editing } = props;
  const projectId = project._id;
  const [updateAgreementsList, setUpdateAgreementsList] = useState(randomString());
  const [deletingAgreement, setDeletingAgreement] = useState<ProjectAgreement>();
  const [sendingSiteLease, setSendingSiteLease] = useState(false);
  /**
   * Deletes an agreement document and refreshes the list.
   * @param projectAgreement - agreement to delete
   */
  const deleteDocument = toastOnError(async (projectAgreement: ProjectAgreement) => {
    await projectAgreementClass.deleteMethod.call(projectAgreement._id);
    toast.success(strings.Deleted());
    setUpdateAgreementsList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setDeletingAgreement(undefined));
  /**
   * Generates a new agreement of the given type and refreshes the list.
   * @param type - agreement type to generate
   */
  const _generateAgreement = toastOnError(async (type: ProjectAgreement['type']) => {
    setSendingSiteLease(true);
    await generateAgreement.call({ projectId, type });
    setUpdateAgreementsList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setSendingSiteLease(false));
  const [sendingAgreement, setSendingAgreement] = useState(false);
  /**
   * Sends an agreement out for e-signature and refreshes the list.
   * @param projectAgreement - agreement to send
   */
  const sendAgreementForSigning = toastOnError(async (projectAgreement: ProjectAgreement) => {
    setSendingAgreement(true);
    const toastId = toast.success(strings.Sending(), { autoClose: MILLIS_1_MIN });
    await sendAgreement.call(projectAgreement._id);
    toast.dismiss(toastId);
    setUpdateAgreementsList(randomString());
  }, strings.AnErrorOccurredPleaseTryAgainOrContactSupport(), () => setDeletingAgreement(undefined));
  const Catalog = useMemo(() => (
    <ListMethodCatalog
      key={updateAgreementsList}
      data={[]}
      columns={[
        { field: 'name' }, { field: 'type', render: (d) => projectAgreementType(d) }, { field: 'createdAt' }, { field: 'status' },
        {
          field: 'actions', label: ' ', render: (d) => {
            return (
              <span className='actions'>
                <a href={fileHref(d.file)} target="_blank" rel="noreferrer"><Icon icon="documentSearch" tip={strings.OpenFile()} /></a>
                <a href="#"><Icon icon="send" onClick={() => !sendingAgreement && sendAgreementForSigning(d)} tip={strings.SendForSigning()} /></a>
                <a href="#"><Icon icon="cancel" onClick={() => setDeletingAgreement(d)} tip={strings.DeleteDocument()} /></a>
              </span>
            );
          }
        },
      ]}
      query={{ projectId }}
      defaultSort={{ createdAt: -1 }}
      listMethod={projectAgreementClass.listMethod}
    />
  ), [updateAgreementsList]);
  return (
    <AgreementsSectionStyled>
      <div className="data-section">
        <CustomerDetails project={project} update={update} setUpdate={setUpdate} editing={editing} />
      </div>
      <div className="send-buttons">
        <div>
          <h3>{strings.GenerateAgreements()}</h3>
          <div className="agreement-buttons">
            <Button onClick={() => _generateAgreement('siteLease')} disabled={sendingSiteLease}>{sendingSiteLease ? <Loading /> : strings.SiteLease()}</Button>
            <Button onClick={() => _generateAgreement('solarPlusBusinessRateCommitment')} disabled={sendingSiteLease}>{sendingSiteLease ? <Loading /> : strings.RateCommitment()}</Button>
          </div>
        </div>
      </div>
      <hr />
      <div className="documents">
        <h3>{strings.Agreements()}</h3>
        <div className="list-wrapper">
          {Catalog}
        </div>
      </div>
      {
        deletingAgreement ?
          <ConfirmModal
            title={strings.DeleteDocument()}
            body={
              <div>{strings.AreYouSureYouWantToDeleteTheDocument()}<br /><b>{deletingAgreement.name}</b></div>
            }
            onClose={() => setDeletingAgreement(undefined)}
            onConfirm={() => deleteDocument(deletingAgreement)}
          />
          :
          null
      }
    </AgreementsSectionStyled>
  );
};

/**
 * Read/edit view of the project's lessor, lessee, owner, tenant, and agreement-term fields.
 */
export const CustomerDetails = (props: ProjectDetailsProps) => {
  const { project, editing, update, setUpdate } = props;
  return (
    <ProjectDetailsStyled>
      <h3>{strings.AgreementInformation()}</h3>
      <div className="infoblock">
        <div className="label">{strings.LessorEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorEmail || project.lessorEmail} onChange={(lessorEmail) => setUpdate({ ...update, lessorEmail })} />
              :
              project.lessorEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorName || project.lessorName} onChange={(lessorName) => setUpdate({ ...update, lessorName })} />
              :
              project.lessorName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorCompany()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorCompany || project.lessorCompany} onChange={(lessorCompany) => setUpdate({ ...update, lessorCompany })} />
              :
              project.lessorCompany || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorCompanyParent()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorCompanyParent || project.lessorCompanyParent} onChange={(lessorCompanyParent) => setUpdate({ ...update, lessorCompanyParent })} />
              :
              project.lessorCompanyParent || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorAddress1()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorAddress1 || project.lessorAddress1} onChange={(lessorAddress1) => setUpdate({ ...update, lessorAddress1 })} />
              :
              project.lessorAddress1 || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorAddress2()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorAddress2 || project.lessorAddress2} onChange={(lessorAddress2) => setUpdate({ ...update, lessorAddress2 })} />
              :
              project.lessorAddress2 || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorAttentionName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorAttentionName || project.lessorAttentionName} onChange={(lessorAttentionName) => setUpdate({ ...update, lessorAttentionName })} />
              :
              project.lessorAttentionName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorAttentionEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorAttentionEmail || project.lessorAttentionEmail} onChange={(lessorAttentionEmail) => setUpdate({ ...update, lessorAttentionEmail })} />
              :
              project.lessorAttentionEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LesseeEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lesseeEmail || project.lesseeEmail} onChange={(lesseeEmail) => setUpdate({ ...update, lesseeEmail })} />
              :
              project.lesseeEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LesseeName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lesseeName || project.lesseeName} onChange={(lesseeName) => setUpdate({ ...update, lesseeName })} />
              :
              project.lesseeName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LesseeCompany()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lesseeCompany || project.lesseeCompany} onChange={(lesseeCompany) => setUpdate({ ...update, lesseeCompany })} />
              :
              project.lesseeCompany || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LesseeCompanyParent()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lesseeCompanyParent || project.lesseeCompanyParent} onChange={(lesseeCompanyParent) => setUpdate({ ...update, lesseeCompanyParent })} />
              :
              project.lesseeCompanyParent || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LessorEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.lessorEmail || project.lessorEmail} onChange={(lessorEmail) => setUpdate({ ...update, lessorEmail })} />
              :
              project.lessorEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PropertyOwnerEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.propertyOwnerEmail || project.propertyOwnerEmail} onChange={(propertyOwnerEmail) => setUpdate({ ...update, propertyOwnerEmail })} />
              :
              project.propertyOwnerEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PropertyOwnerName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.propertyOwnerName || project.propertyOwnerName} onChange={(propertyOwnerName) => setUpdate({ ...update, propertyOwnerName })} />
              :
              project.propertyOwnerName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.TenantEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.tenantEmail || project.tenantEmail} onChange={(tenantEmail) => setUpdate({ ...update, tenantEmail })} />
              :
              project.tenantEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.TenantName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.tenantName || project.tenantName} onChange={(tenantName) => setUpdate({ ...update, tenantName })} />
              :
              project.tenantName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.CeaEmail()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.ceaEmail || project.ceaEmail} onChange={(ceaEmail) => setUpdate({ ...update, ceaEmail })} />
              :
              project.ceaEmail || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.CeaName()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.ceaName || project.ceaName} onChange={(ceaName) => setUpdate({ ...update, ceaName })} />
              :
              project.ceaName || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.LeaseEffectiveDate()}</div>
        <div className="value">
          {
            editing ?
              <DateInput value={update.leaseEffectiveDate || project.leaseEffectiveDate} onChange={(leaseEffectiveDate) => setUpdate({ ...update, leaseEffectiveDate })} />
              :
              project.leaseEffectiveDate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PtoDate()}</div>
        <div className="value">
          {
            editing ?
              <DateInput value={update.ptoDate || project.ptoDate} onChange={(ptoDate) => setUpdate({ ...update, ptoDate })} />
              :
              project.ptoDate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.TermEndDate()}</div>
        <div className="value">
          {
            editing ?
              <DateInput value={update.termEndDate || project.termEndDate} onChange={(termEndDate) => setUpdate({ ...update, termEndDate })} />
              :
              project.termEndDate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.MaxTermEndDate()}</div>
        <div className="value">
          {
            editing ?
              <DateInput value={update.maxTermEndDate || project.maxTermEndDate} onChange={(maxTermEndDate) => setUpdate({ ...update, maxTermEndDate })} />
              :
              project.maxTermEndDate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.AgreementExpirationDate()}</div>
        <div className="value">
          {
            editing ?
              <DateInput value={update.agreementExpirationDate || project.agreementExpirationDate} onChange={(agreementExpirationDate) => setUpdate({ ...update, agreementExpirationDate })} />
              :
              project.agreementExpirationDate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.MeterNumbers()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.meterNumbers || project.meterNumbers} onChange={(meterNumbers) => setUpdate({ ...update, meterNumbers })} />
              :
              project.meterNumbers || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.InitialRate()}</div>
        <div className="value">
          {
            editing ?
              <NumberInput value={update.initialRate || project.initialRate} onChange={(initialRate) => setUpdate({ ...update, initialRate })} step="0.001" />
              :
              project.initialRate || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.InitialRateUpdated()}</div>
        <div className="value">
          {
            editing ?
              <NumberInput value={update.initialRateUpdated || project.initialRateUpdated} onChange={(initialRateUpdated) => setUpdate({ ...update, initialRateUpdated })} step="0.001" />
              :
              project.initialRateUpdated || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.FirstRentPayment()}</div>
        <div className="value">
          {
            editing ?
              <NumberInput value={update.firstRentPayment || project.firstRentPayment} onChange={(firstRentPayment) => setUpdate({ ...update, firstRentPayment })} step="0.01" />
              :
              project.firstRentPayment || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.ProCo()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.proCo || project.proCo} onChange={(proCo) => setUpdate({ ...update, proCo })} />
              :
              project.proCo || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PropertyLegalDescription()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.propertyLegalDescription || project.propertyLegalDescription} onChange={(propertyLegalDescription) => setUpdate({ ...update, propertyLegalDescription })} />
              :
              project.propertyLegalDescription || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.EasementsMetesAndBounds()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.easementsMetesAndBounds || project.easementsMetesAndBounds} onChange={(easementsMetesAndBounds) => setUpdate({ ...update, easementsMetesAndBounds })} />
              :
              project.easementsMetesAndBounds || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.CustomerUtilityMeterNumbers()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.customerUtilityMeterNumbers || project.customerUtilityMeterNumbers} onChange={(customerUtilityMeterNumbers) => setUpdate({ ...update, customerUtilityMeterNumbers })} />
              :
              project.customerUtilityMeterNumbers || 'N/A'
          }
        </div>
      </div>
      <div className="infoblock">
        <div className="label">{strings.PropertyApn()}</div>
        <div className="value">
          {
            editing ?
              <Input value={update.propertyApn || project.propertyApn} onChange={(propertyApn) => setUpdate({ ...update, propertyApn })} />
              :
              project.propertyApn || 'N/A'
          }
        </div>
      </div>
    </ProjectDetailsStyled>
  );
};
